// @owners { team: patient-health }
import { ActionSheetContext, Body, Button, Column, H3, Row, SmSpacing, XsSpacing } from '@alto/design-system';
import { RenewalCancelActionSheet, RenewalRequestActionSheet, RenewalRequestStatusActionSheet } from '@alto/features';
import React from 'react';
import { openModal } from '~shared/actions/modal';
import { ineligibleRenewalText, prescriptionRenewalHasStarted } from '~shared/features/my-meds/helper';
import {
  RENEWAL_STATE_ELIGIBLE,
  RENEWAL_STATE_INELIGIBLE,
  RENEWAL_STATE_PENDING,
  type RenewalState,
} from '~shared/features/my-meds/selectors/getRenewalStateForPrescription';
import { formatDateWithDowAndYear } from '~shared/helpers/date';
import { sendAnalyticsEvent } from '~shared/lib/analytics/src/actions';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { useDispatchShared } from '~shared/store';
import { type Prescription } from '~shared/types/clients';

export type RenewalProps = {
  readonly prescription: Prescription;
  readonly renewalState: RenewalState;
};

export const Renewal = ({ prescription, renewalState }: RenewalProps) => {
  const dispatch = useDispatchShared();
  const { setActiveActionSheet } = React.useContext(ActionSheetContext);
  const prescriptionStarted = prescriptionRenewalHasStarted(prescription);
  const openArchiveModal = () => {
    dispatch(openModal('ARCHIVE_PRESCRIPTION_MODAL', { prescription }));
    dispatch(
      sendAnalyticsEvent(
        createEvent(EVENTS.MED_DETAILS__ARCHIVE_CONFIRMATION_VIEWED, {}, { prescriptionId: prescription.id }),
      ),
    );
  };

  return (
    <Row>
      {/* Eligible */}
      {renewalState === RENEWAL_STATE_ELIGIBLE && (
        <Column>
          <H3>Need more refills?</H3>
          <Body>
            If you still need this medication, request more refills from your doctor. If you no longer need it, archive
            it.
          </Body>
          <SmSpacing />
          <Row>
            <Button
              onPress={() => {
                setActiveActionSheet(<RenewalRequestActionSheet prescriptionIDs={[prescription.id]} />);
              }}
              label="Request more refills"
            />
            <XsSpacing />
            <Button
              type="secondary"
              onPress={openArchiveModal}
              label="Archive this prescription"
            />
          </Row>
        </Column>
      )}

      {/* Pending */}
      {renewalState === RENEWAL_STATE_PENDING && (
        <Column>
          <H3>{prescriptionStarted ? 'Refills requested!' : 'Refill request in progress'}</H3>
          <Body>
            {prescriptionStarted
              ? 'Your refill request has been sent to your doctor. We will let you know when we hear back.'
              : `We will ask your doctor for more refills for this on ${formatDateWithDowAndYear(
                  prescription.active_renewal_request_start_at,
                )}. We will let you know as soon as we hear back.`}
          </Body>
          <SmSpacing />
          <Row spaceBetween>
            <Button
              label={prescriptionStarted ? 'View request status' : 'View refill request details'}
              onPress={() => {
                setActiveActionSheet(<RenewalRequestStatusActionSheet prescription={prescription} />);
              }}
            />
            <XsSpacing />
            <Button
              variant="danger"
              label="Cancel refill request"
              onPress={() => {
                setActiveActionSheet(<RenewalCancelActionSheet prescription={prescription} />);
              }}
            />
          </Row>
        </Column>
      )}

      {/* Ineligible */}
      {renewalState === RENEWAL_STATE_INELIGIBLE && (
        <Row>
          <H3>Need more refills?</H3>
          <Body>{ineligibleRenewalText(prescription)}</Body>
        </Row>
      )}
    </Row>
  );
};
